import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

const slugify = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const initialForm = {
  name: "",
  slug: "",
  description: "",
  accentColor: "",
  sortOrder: "0",
  featured: false,
};

const MarketplaceCategoryDialog = ({
  open,
  category = null,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState(initialForm);
  const [slugManual, setSlugManual] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSlugManual(false);
    if (category) {
      setForm({
        name: category.name || "",
        slug: category.slug || "",
        description: category.description || "",
        accentColor: category.accentColor || "",
        sortOrder: String(category.sortOrder ?? 0),
        featured: !!category.featured,
      });
    } else {
      setForm(initialForm);
    }
  }, [open, category]);

  const handleChange = (field) => (event) => {
    const value = field === "featured" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleNameChange = (event) => {
    const name = event.target.value;
    setForm((prev) => {
      const next = { ...prev, name };
      if (!category && !slugManual) {
        next.slug = slugify(name);
      }
      return next;
    });
  };

  const handleSlugChange = (event) => {
    setSlugManual(true);
    setForm((prev) => ({ ...prev, slug: event.target.value }));
  };

  const handleSubmit = () => {
    const name = String(form.name || "").trim();
    const slug = slugify(form.slug || form.name);
    if (!name || !slug) return;
    const sortOrder = Number.parseInt(String(form.sortOrder), 10);
    const payload = {
      name,
      slug,
      description: form.description?.trim() || null,
      accentColor: form.accentColor?.trim() || null,
      sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
      featured: !!form.featured,
    };
    if (category) {
      onSubmit({ id: category.id, ...payload });
    } else {
      onSubmit(payload);
    }
  };

  const title = category ? "Edit Marketplace Category" : "Add Marketplace Category";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Categories group products in the marketplace and on the member app. Slug must be unique (lowercase, hyphens).
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2 }}>
        <FormControl fullWidth>
          <TextField label="Name" value={form.name} onChange={handleNameChange} size="small" required />
        </FormControl>
        <FormControl fullWidth>
          <TextField
            label="Slug"
            value={form.slug}
            onChange={handleSlugChange}
            size="small"
            required
            helperText={category ? "Changing slug affects URLs if referenced elsewhere." : "Auto-filled from name; edit if needed."}
          />
        </FormControl>
        <FormControl fullWidth>
          <TextField
            label="Description"
            value={form.description}
            onChange={handleChange("description")}
            multiline
            minRows={2}
            size="small"
          />
        </FormControl>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 2 }}>
          <FormControl fullWidth>
            <TextField
              label="Accent color"
              value={form.accentColor}
              onChange={handleChange("accentColor")}
              size="small"
              placeholder="#8B5CF6"
            />
          </FormControl>
          <FormControl fullWidth>
            <TextField
              label="Sort order"
              value={form.sortOrder}
              onChange={handleChange("sortOrder")}
              size="small"
              type="number"
              inputProps={{ step: 1 }}
            />
          </FormControl>
        </Box>
        <FormControlLabel
          control={<Checkbox checked={form.featured} onChange={handleChange("featured")} size="small" />}
          label="Featured category"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {category ? "Update Category" : "Add Category"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MarketplaceCategoryDialog;
